"use client"

import { useState, useEffect, useRef, Suspense, useCallback } from "react"
import { usePrerenderSafeSearchParams } from "@/hooks/use-prerender-safe-search-params"
import { SearchParamsHydrationGate } from "@/components/ssr/search-params-hydration-gate"
import { motion } from "motion/react"
import { Sprout, ArrowRight } from "lucide-react"
import {
  WeedAndFeedForm,
  type WeedAndFeedCurrentValues,
  type WeedAndFeedInitialValues,
} from "@/components/tools/weed-and-feed-form"
import {
  HeadlineHero,
  RelatedTools,
  ResultEmailCaptureCard,
  ToolPageLoadingFallback,
} from "@/components/tools/shared"
import { BreadcrumbComponent } from "@/components/ui/breadcrumb"
import { getWeedAndFeedProduct, type GrassId } from "@/data/weed-and-feed-products"
import type { WeedAndFeedResult } from "@/lib/weed-and-feed-calculator"
import { analytics, stashEmailCaptureAttribution, trackEvent } from "@/lib/analytics"
import { useImpressionEvent } from "@/hooks/useImpressionEvent"
import { buildToolUrl } from "@/lib/result-tool-urls"
import { useCalculatorStarted } from "@/hooks/useCalculatorStarted"
import { FADE_UP, FADE_TRANSITION } from "@/lib/motion-presets"
import { Reveal } from "@/components/motion"

const GRASS_PARAM: Record<string, GrassId> = {
  bermuda: "bermuda",
  bermudagrass: "bermuda",
  zoysia: "zoysia",
  zoysiagrass: "zoysia",
  staug: "staug",
  staugustine: "staug",
  staugustinegrass: "staug",
  centipede: "centipede",
  centipedegrass: "centipede",
  fescue: "fescue",
  tallfescue: "fescue",
  kbg: "kbg",
  kentuckybluegrass: "kbg",
  bluegrass: "kbg",
}

const LAWN_SIZE_MIN = 100
const LAWN_SIZE_MAX = 217800

function normalizeGrass(raw: string): GrassId | undefined {
  const key = raw.toLowerCase().replace(/[-_.\s]+/g, "")
  return GRASS_PARAM[key]
}

function parseLawnSizeParam(raw: string | null): number | undefined {
  if (!raw || !/^\d+$/.test(raw)) return undefined
  const size = Number(raw)
  if (!Number.isSafeInteger(size) || size < LAWN_SIZE_MIN || size > LAWN_SIZE_MAX) {
    return undefined
  }
  return size
}

const GRASS_ID_CTA_LOCATION = "weed_and_feed_grass_id_cross_sell"

function WeedAndFeedContent() {
  const searchParams = usePrerenderSafeSearchParams()
  const [initialValues, setInitialValues] = useState<WeedAndFeedInitialValues | null>(null)
  const [currentValues, setCurrentValues] = useState<WeedAndFeedCurrentValues | null>(null)
  const [liveResult, setLiveResult] = useState<WeedAndFeedResult | null>(null)
  const trackedRef = useRef(false)
  // calculator_started on first input — result is gated on a known region (ZIP).
  const startedRef = useCalculatorStarted('weed-and-feed')

  // URL prefill: ?zip & size & grass & product
  useEffect(() => {
    const zipParam = searchParams.get("zip")
    const sizeParam = searchParams.get("size")
    const grassParam = searchParams.get("grass")
    const productParam = searchParams.get("product")

    if (!zipParam && !sizeParam && !grassParam && !productParam) return

    const size = parseLawnSizeParam(sizeParam)
    const next: WeedAndFeedInitialValues = {
      zipCode: zipParam && /^\d{5}$/.test(zipParam) ? zipParam : undefined,
      lawnSize: size,
      grassType: grassParam ? normalizeGrass(grassParam) : undefined,
      productId: productParam && getWeedAndFeedProduct(productParam) ? productParam : undefined,
    }
    setInitialValues(next)
  }, [searchParams])

  const handleResultChange = useCallback((result: WeedAndFeedResult, values: WeedAndFeedCurrentValues) => {
    setLiveResult(result)
    setCurrentValues(values)
  }, [])

  // Fire calculator_used once, when a usable result (known region) first appears.
  useEffect(() => {
    if (!liveResult || trackedRef.current || !liveResult.regionKnown) return
    trackedRef.current = true
    analytics.calculatorUsed("weed-and-feed", {
      inputs: {
        product_id: liveResult.product.id,
        region: liveResult.region,
      },
      result: {
        safe: liveResult.safety.safe,
        amount_kind: liveResult.amount?.kind ?? undefined,
      },
      source: "live_update",
    })
  }, [liveResult])

  // The grass-ID flow lives on the homepage funnel.
  const grassIdHref = "/"

  // Impression denominator for the cross-sell's cta_clicked. Same cta_location
  // string on both events so PostHog can compute the rate.
  const grassIdCtaRef = useImpressionEvent<HTMLDivElement>(
    GRASS_ID_CTA_LOCATION,
    { cta_text: "Identify my grass" },
    { emit: analytics.ctaSurfaceShown },
  )

  return (
    <div ref={startedRef} className="min-h-screen bg-linear-to-br from-green-50 via-white to-gray-50">
      <Reveal onMount y={6} className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 pt-4 md:pt-8 pb-8 md:pb-16">
        <BreadcrumbComponent
          items={[
            { label: "Home", href: "/" },
            { label: "Tools", href: "/tools" },
            { label: "Weed & Feed Calculator" },
          ]}
        />

        <div className="max-w-7xl mx-auto">
          <div className="relative mb-8 md:mb-12">
            <HeadlineHero
              eyebrow="Timing by ZIP code"
              title="When to apply weed & feed, and"
              accent="how much to buy"
              subtitle="Enter your ZIP, pick your product, and add your lawn size. You'll get your regional timing windows, bags or quarts needed, spreader settings, and grass-safety checks."
            />
          </div>

          <div className="mb-8">
            <WeedAndFeedForm initialValues={initialValues} onResultChange={handleResultChange} />
          </div>

          {liveResult && liveResult.regionKnown && (
            <div className="mb-8">
              <ResultEmailCaptureCard
                source="tool_result_weed_and_feed"
                toolDisplayName="weed & feed schedule"
                subcopy="Spring and fall application reminders for your region, sent to your inbox."
                zipCode={currentValues?.zipCode ?? initialValues?.zipCode}
                accountLead={{
                  redirectTo: buildToolUrl("weed-and-feed-calculator", {
                    zip: currentValues?.zipCode ?? initialValues?.zipCode,
                    lawnSize: currentValues?.lawnSize ?? initialValues?.lawnSize,
                  }),
                  headline: "Save my weed & feed schedule",
                  subcopy: "Keep this weed & feed schedule in a free account and track your lawn over time.",
                  onBeforeOAuth: () => stashEmailCaptureAttribution("tool_result_weed_and_feed"),
                }}
                successSubcopy="Your lawn tips are on the way. Your weed & feed schedule stays right here to revisit anytime."
                onCaptured={() => {}}
              />
            </div>
          )}

          {/* Grass-ID cross-sell (design spec): weed & feed safety hinges on
              knowing your grass, so route unsure users to the ID flow. */}
          <div
            ref={grassIdCtaRef}
            className="mb-8 flex flex-wrap items-center gap-4 rounded-2xl border border-gray-200 bg-white px-6 py-5"
          >
            <Sprout className="h-6 w-6 shrink-0 text-green-600" />
            <p className="m-0 min-w-[240px] flex-1 text-sm text-gray-700">
              <strong className="text-gray-900">Not sure what grass you have?</strong> Snap a photo and we&apos;ll
              identify it, then check it against your weed &amp; feed automatically.
            </p>
            <a
              href={grassIdHref}
              onClick={() =>
                trackEvent("cta_clicked", {
                  cta_location: GRASS_ID_CTA_LOCATION,
                  cta_text: "Identify my grass",
                })
              }
              className="inline-flex min-h-11 items-center gap-2 whitespace-nowrap rounded-full border border-green-200 bg-green-50 px-5 font-bold text-green-800 no-underline"
            >
              Identify my grass
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>

          <RelatedTools
            currentToolId="weed-and-feed-calculator"
            prefillValues={{
              zip: currentValues?.zipCode ?? initialValues?.zipCode,
              lawnSize: currentValues?.lawnSize ?? initialValues?.lawnSize,
            }}
          />
        </div>
      </Reveal>
    </div>
  )
}

export function WeedAndFeedCalculatorPageContent() {
  return (
    <Suspense fallback={<ToolPageLoadingFallback toolId="weed-and-feed-calculator" />}>
      <SearchParamsHydrationGate dateSensitive>
        <WeedAndFeedContent />
      </SearchParamsHydrationGate>
    </Suspense>
  )
}
